import { useState } from 'react';
import { notifications } from '@mantine/notifications';
import { IconCheck } from '@tabler/icons-react';
import fileCommisionDeviationMutations from '@/api/mutitations/fileCommisionDeviations';
import PageTemplate from '@/components/PageTemplate';
import Table from '@/components/Table';
import prepareTranslate from '@/helpers/dictionary';
import { extractFilterOptions } from '@/components/Table/Table.functions';

const FileCommissionDeviations = () => {
  const t = prepareTranslate();

  const [filterOptions, setFilterOptions] = useState({});
  const [columnFilters, setColumnFilters] = useState({});
  const [refreshKey, setRefreshKey] = useState(0);
  const [approvingMultiple, setApprovingMultiple] = useState(false);

  const { mutateAsync: updateMultipleMutation } = fileCommisionDeviationMutations
    .useUpdateMultiple();
  const { mutateAsync: getAllMutation } = fileCommisionDeviationMutations.useGetAll();

  const handleApproveMultiple = async (selectedRows) => {
    if (approvingMultiple || !selectedRows || selectedRows.length === 0) return;

    setApprovingMultiple(true);

    try {
      const ids = selectedRows.map((row) => row._id);

      await updateMultipleMutation({
        ids,
        updateData: {
          isApproved: true,
        },
      });

      setRefreshKey((prev) => prev + 1);
    } catch (error) {
      notifications.show({
        title: t('pages.fileCommissionDeviations.notifications.errors.approveMultiple.title'),
        message: t('pages.fileCommissionDeviations.notifications.errors.approveMultiple.message'),
        color: 'red',
      });
    } finally {
      setApprovingMultiple(false);
    }
  };

  const columns = [
    {
      title: t('pages.fileCommissionDeviations.columns.company'),
      accessor: 'company.hebrew',
      sortable: true,
    },
    {
      title: t('pages.fileCommissionDeviations.columns.product'),
      accessor: 'product.hebrew',
      sortable: true,
    },
    {
      title: t('pages.fileCommissionDeviations.columns.year'),
      accessor: 'year',
      sortable: true,
      isFilterActive: true,
    },
    {
      title: t('pages.fileCommissionDeviations.columns.identifiernNumber'),
      accessor: 'identifiernNumber',
      sortable: true,
      isFilterActive: true,
    },
    {
      title: t('pages.fileCommissionDeviations.columns.actualValue'),
      accessor: 'actualValue',
      sortable: true,
      transforms: [{ func: 'round', params: { digits: 2 } }],
      isFilterActive: true,
    },
    {
      title: t('pages.fileCommissionDeviations.columns.expectedValue'),
      accessor: 'expectedValue',
      sortable: true,
      transforms: [{ func: 'round', params: { digits: 2 } }],
      isFilterActive: true,
    },
    {
      title: t('pages.fileCommissionDeviations.columns.deviationValue'),
      accessor: 'deviationValue',
      sortable: true,
      transforms: [{ func: 'round', params: { digits: 2 } }],
      isFilterActive: true,
    },
    {
      title: t('pages.fileCommissionDeviations.columns.column'),
      accessor: 'column',
      sortable: true,
      isFilterActive: true,
    },
    {
      title: t('pages.fileCommissionDeviations.columns.rowNumber'),
      accessor: 'rowNumber',
      sortable: true,
      isFilterActive: true,
    },
  ];

  const getData = async (start, end, query, sort) => {
    try {
      const response = await getAllMutation({
        start,
        end,
        sort,
        query: {
          ...query,
          isApproved: false,
        },
      });

      const updatedData = response.data.map((item) => ({
        ...item,
        id: item._id,
      }));

      const options = extractFilterOptions(updatedData, columns);

      setFilterOptions(options);

      return { ...response, data: updatedData };
    } catch (error) {
      notifications.show({
        title: t('pages.fileCommissionDeviations.notifications.errors.find.title'),
        message: t('pages.fileCommissionDeviations.notifications.errors.find.message'),
        color: 'red',
      });

      throw error;
    }
  };

  const mockSelectionActions = [
    {
      title: t('pages.fileCommissionDeviations.actions.approveMultiple'),
      icon: <IconCheck stroke="1" />,
      onClick: handleApproveMultiple,
      loading: approvingMultiple,
    },
  ];

  return (
    <PageTemplate title={t('pages.fileCommissionDeviations.title')}>
      <Table
        key={refreshKey}
        columns={columns}
        getData={getData}
        columnFilters={columnFilters}
        setColumnFilters={setColumnFilters}
        filterOptions={filterOptions}
        isExportActive={false}
        isColumnsActive={false}
        showSelectionColumn={true}
        selectionActions={mockSelectionActions}
      />
    </PageTemplate>
  );
};

export default FileCommissionDeviations;
